import { Emitter, EmitterConfigV3 } from "@pixi/particle-emitter";
import { Container, Texture, Ticker } from "pixi.js";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";

export class CubeExplowAnimationView extends Container {
    public static readonly BLOW_EMIT_DURATION: number = 300/*ms*/;

    private readonly vcs: PromiseCompletionSource<void>;
    private readonly emitter: Emitter;
    private readonly particlesLifetimeMs: number;

    private animationTimeMs: number;

    constructor(textures: Array<Texture>) {
        super();
        const config = this.setupAnimation(textures);
        this.emitter = new Emitter(this, config);
        this.particlesLifetimeMs = config.lifetime.max * 1000;
        this.animationTimeMs = 0;
        this.vcs = new PromiseCompletionSource();
    }

    private setupAnimation(textures: Array<Texture>): EmitterConfigV3 {
        return {
            "lifetime": {
                "min": 0.8,
                "max": 2.0
            },
            "frequency": 0.004,
            "emitterLifetime": 0.5,
            "maxParticles": 500,
            "addAtBack": false,
            "pos": {
                "x": 0,
                "y": 0
            },
            "behaviors": [
                {
                    "type": 'moveSpeed',
                    "config": {
                        "speed": {
                            "list": [{ value: 500, time: 0 }, { value: 100, time: 0.25 }, { value: 100, time: 1 }],
                        },
                        "minMult": 0.8
                    }
                },
                {
                    "type": "rotation",
                    "config": {
                        "minStart": 230,
                        "maxStart": 310,
                        "minSpeed": 0,
                        "maxSpeed": 50,
                        "accel": 50
                    }
                },
                {
                    "type": 'alpha',
                    "config": {
                        "alpha": {
                            "list": [{ value: 1, time: 0 }, { value: 0.5, time: 0.25 }, { value: 0.08, time: 1 }]
                        },
                    }
                },
                {
                    "type": "animatedSingle",
                    "config": {
                        "anim": {
                            "loop": true,
                            "textures": textures
                        }
                    }
                },
                {
                    "type": "spawnShape",
                    "config": {
                        "type": 'rect',
                        "data": {
                            "x": 0,
                            "y": 0,
                            "w": 192,
                            "h": 0
                        }
                    }
                }, {
                    "type": 'scale',
                    "config": {
                        "scale": {
                            "list": [{ value: 0.8, time: 0 }, { value: 0.7, time: 0.5 }, { value: 0.4, time: 1 }],
                            "isStepped": false
                        },
                        "minMult": 0.5
                    }
                }
            ]
        }
    }

    public async play(): Promise<void> {
        this.emitter.autoUpdate = true;
        Ticker.shared.add(this.updateTime, this);
        await this.vcs.result;
    }

    public async continue(): Promise<void> {
        this.emitter.autoUpdate = true;
        Ticker.shared.add(this.updateTime, this);
        await this.vcs.result;
    }

    public stop(): void {
        Ticker.shared.remove(this.updateTime, this);
        this.emitter.autoUpdate = false;
    }

    private updateTime(): void {
        this.animationTimeMs += Ticker.shared.elapsedMS;
        if (this.animationTimeMs >= this.particlesLifetimeMs) {
            this.emitter.autoUpdate = false;
            Ticker.shared.remove(this.updateTime, this);
            this.vcs.complete();
        }
    }
}